import { useParams, Navigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { HowItWorks } from "@/components/HowItWorks";
import { FinalCTA } from "@/components/FinalCTA";
import { InternalLinks } from "@/components/InternalLinks";
import { Button } from "@/components/ui/button";
import { Star, CheckCircle2, Clock, Sparkles, Zap, Calendar } from "lucide-react";
import { PlanComparison } from "@/components/PlanComparison";
import { Guarantee } from "@/components/Guarantee";
import { SEO } from "@/components/SEO";
import { useLocation } from "@/lib/locations";
import { getLocalContent } from "@/lib/city-content";

const BOOKING_URL = "/book";

const getServiceData = (locationName: string) => ({
  "standard-clean": {
    title: "Standard Home Cleaning",
    description: `The perfect maintenance clean for busy ${locationName} families. We cover all the essentials to keep your home fresh and tidy.`,
    keywords: `standard cleaning ${locationName}, maid service ${locationName}, regular house cleaning`,
    checklistPoints: ["Dusting all surfaces", "Vacuuming & Mopping", "Kitchen cleaning", "Bathroom sanitization", "Trash removal"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/d3b447ae-8f3f-4b3a-aad0-2305e1370261.png"
  },
  "deep-clean": {
    title: "Deep Cleaning Services",
    description: `A top-to-bottom thorough clean of your entire home. Ideal for first-time visits or seasonal refreshes in the ${locationName} area.`,
    keywords: `deep cleaning ${locationName}, thorough house cleaning, professional deep cleaners`,
    checklistPoints: ["Inside microwave/appliances", "Baseboards & trim", "Detailed grout scrubbing", "Window sills & tracks", "Cabinet fronts"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/45693ca3-057a-4ac9-8c3f-50686ae15984.png"
  },
  "move-out-clean": {
    title: "Move In / Move Out Cleaning",
    description: `Moving in ${locationName}? Let us handle the cleaning so you can focus on the move. We leave every corner spotless for the next resident.`,
    keywords: `move out cleaning ${locationName}, move in cleaning services, vacancy cleaning`,
    checklistPoints: ["Inside all cabinets", "Inside oven & fridge", "Full wall dusting", "Closet cleaning", "All fixtures & switches"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/d9009cf2-193c-419e-ab3f-14ed63ffbc07.png"
  },
  "post-construction-clean": {
    title: "Post Construction Cleaning",
    description: `Just finished a renovation in ${locationName}? Our post-construction team removes all dust, debris, and residue so your space is move-in ready.`,
    keywords: `post construction cleaning ${locationName}, renovation cleaning, new build cleaning`,
    checklistPoints: ["Industrial vacuuming", "Wall & ceiling dusting", "Window cleaning", "Debris removal", "75pt Checklist"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/11212d20-4d72-4d68-a362-89a23c8ee0fc.png"
  },
  "commercial-clean": {
    title: "Commercial Cleaning",
    description: `Professional cleaning solutions for offices, retail spaces, and commercial properties across ${locationName}. After-hours scheduling available.`,
    keywords: `commercial cleaning ${locationName}, office cleaning ${locationName}, retail cleaning`,
    checklistPoints: ["Customized scope", "After-hours scheduling", "Retail & office focus", "Recurring contracts", "Disinfection services"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/1e0c0a4e-0a0c-4858-800e-037e76674ae3.jpg"
  },
  "weekly-clean": {
    title: "Weekly Cleaning Service",
    description: `Keep your ${locationName} home consistently spotless with our weekly cleaning service. Perfect for busy families who want zero cleaning stress.`,
    keywords: `weekly cleaning service ${locationName}, weekly maid service, recurring house cleaning`,
    checklistPoints: ["Consistent weekly schedule", "Dedicated cleaning team", "Kitchen & bathroom focus", "Floor care", "Dusting & tidying"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/d3b447ae-8f3f-4b3a-aad0-2305e1370261.png"
  },
  "biweekly-clean": {
    title: "Bi-Weekly Cleaning Service",
    description: `Our most popular option in ${locationName}. A thorough bi-weekly clean to maintain your home's sparkle without breaking the bank.`,
    keywords: `bi-weekly cleaning service ${locationName}, every two weeks cleaning, regular maid service`,
    checklistPoints: ["Every 14 days schedule", "Full home maintenance", "Kitchen deep clean", "Bathroom sanitization", "Floor vacuuming & mopping"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/45693ca3-057a-4ac9-8c3f-50686ae15984.png"
  },
  "recurring-clean": {
    title: "Recurring Cleaning Service",
    description: `Set it and forget it. Our recurring cleaning services in ${locationName} give you your weekends back.`,
    keywords: `recurring cleaning service ${locationName}, scheduled house cleaning, ongoing maid service`,
    checklistPoints: ["Weekly, bi-weekly, or monthly", "Discounted rates", "Consistent quality", "Priority scheduling", "Customized cleaning plan"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/d3b447ae-8f3f-4b3a-aad0-2305e1370261.png"
  },
  "one-time-clean": {
    title: "One-Time Cleaning Service",
    description: `Need a quick refresh in ${locationName}? Our one-time cleaning service is perfect for catching up or preparing for guests.`,
    keywords: `one time cleaning service ${locationName}, single house cleaning, spring cleaning`,
    checklistPoints: ["No contracts required", "Thorough deep clean", "Kitchen & baths", "Living areas", "Instant online booking"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/45693ca3-057a-4ac9-8c3f-50686ae15984.png"
  },
  "same-day-clean": {
    title: "Same-Day Cleaning Service",
    description: `Emergency cleaning needed in ${locationName}? We offer same-day and next-day cleaning services when available.`,
    keywords: `same day cleaning service ${locationName}, emergency house cleaning, last minute maid service`,
    checklistPoints: ["Fast response time", "Emergency cleanups", "Guest preparation", "Move-in emergencies", "Full team dispatch"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/d3b447ae-8f3f-4b3a-aad0-2305e1370261.png"
  },
  "apartment-clean": {
    title: "Apartment Cleaning Service",
    description: `Specialized apartment and condo cleaning in ${locationName}. We navigate building rules and deliver a spotless space.`,
    keywords: `apartment cleaning service ${locationName}, condo cleaning, small space cleaning`,
    checklistPoints: ["Efficient small space cleaning", "Building rule compliance", "Kitchen & bath focus", "Balcony glass (interior)", "Floor care"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/45693ca3-057a-4ac9-8c3f-50686ae15984.png"
  },
  "move-in-clean": {
    title: "Move-In Cleaning Service",
    description: `Start fresh in your new ${locationName} home. We sanitize and deep clean before your boxes arrive.`,
    keywords: `move in cleaning service ${locationName}, new home cleaning, pre-move cleaning`,
    checklistPoints: ["Inside all cabinets", "Inside appliances", "Full sanitization", "Baseboards & blinds", "Floor deep clean"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/d9009cf2-193c-419e-ab3f-14ed63ffbc07.png"
  },
  "airbnb-clean": {
    title: "Airbnb / Rental Turnover Cleaning",
    description: `Reliable Airbnb and vacation rental turnover cleaning in ${locationName}. 5-star reviews start with a spotless property.`,
    keywords: `airbnb cleaning ${locationName}, vacation rental cleaning, turnover cleaning service`,
    checklistPoints: ["Fast turnover times", "Linen changing", "Restocking supplies", "Damage reporting", "5-star staging"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/1e0c0a4e-0a0c-4858-800e-037e76674ae3.jpg"
  },
  "pet-friendly-clean": {
    title: "Cleaning for Pet Owners",
    description: `We love pets! Our ${locationName} pet-friendly cleaning service focuses on hair removal, dander, and pet-safe products.`,
    keywords: `pet friendly cleaning ${locationName}, house cleaning for pet owners, pet hair removal`,
    checklistPoints: ["Pet hair removal", "Pet-safe products", "Odor neutralization", "Furniture vacuuming", "Floor care"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/d3b447ae-8f3f-4b3a-aad0-2305e1370261.png"
  },
  "eco-friendly-clean": {
    title: "Eco-Friendly Cleaning Service",
    description: `Green cleaning services in ${locationName} using non-toxic, eco-friendly products safe for your family and the planet.`,
    keywords: `eco friendly cleaning ${locationName}, green house cleaning, non toxic maid service`,
    checklistPoints: ["Non-toxic products", "Harsh chemical free", "Safe for kids & pets", "Eco-friendly practices", "Allergen reduction"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/45693ca3-057a-4ac9-8c3f-50686ae15984.png"
  },
  "window-cleaning": {
    title: "Window Cleaning (Coming Soon)",
    description: `Professional exterior and interior window cleaning services expanding soon to ${locationName}. Join our waitlist!`,
    keywords: `window cleaning ${locationName}, exterior window washing, glass cleaning`,
    checklistPoints: ["Interior & exterior glass", "Screen cleaning", "Track vacuuming", "Streak-free finish", "Multi-story capable"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/11212d20-4d72-4d68-a362-89a23c8ee0fc.png"
  },
  "pressure-washing": {
    title: "Pressure Washing (Coming Soon)",
    description: `Revitalize your home's exterior in ${locationName}. Our pressure washing services are launching soon.`,
    keywords: `pressure washing ${locationName}, power washing, driveway cleaning`,
    checklistPoints: ["Driveway cleaning", "Sidewalk washing", "House siding soft wash", "Patio & deck cleaning", "Stain removal"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/11212d20-4d72-4d68-a362-89a23c8ee0fc.png"
  },
  "gutter-cleaning": {
    title: "Gutter Cleaning (Coming Soon)",
    description: `Protect your ${locationName} home from water damage. Professional gutter cleaning services expanding soon.`,
    keywords: `gutter cleaning ${locationName}, roof cleaning, downspout clearing`,
    checklistPoints: ["Debris removal", "Downspout flushing", "Inspection", "Mess-free process", "Seasonal maintenance"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/11212d20-4d72-4d68-a362-89a23c8ee0fc.png"
  },
  "solar-panel-cleaning": {
    title: "Solar Panel Cleaning (Coming Soon)",
    description: `Maximize your energy output in ${locationName} with professional solar panel cleaning. Coming soon!`,
    keywords: `solar panel cleaning ${locationName}, solar washing, energy efficiency`,
    checklistPoints: ["Pure water system", "Soft bristle brushes", "No harsh chemicals", "Output optimization", "Safe access"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/11212d20-4d72-4d68-a362-89a23c8ee0fc.png"
  },
  "junk-removal": {
    title: "Junk Removal (Coming Soon)",
    description: `Declutter your ${locationName} home fast. Professional junk removal services launching soon.`,
    keywords: `junk removal ${locationName}, trash hauling, decluttering service`,
    checklistPoints: ["Furniture removal", "Appliance hauling", "Garage cleanouts", "Eco-friendly disposal", "Same-day options"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/11212d20-4d72-4d68-a362-89a23c8ee0fc.png"
  },
  "car-detailing": {
    title: "Mobile Car Detailing (Coming Soon)",
    description: `We're bringing our cleaning expertise to your driveway in ${locationName}. Mobile car detailing coming soon.`,
    keywords: `mobile car detailing ${locationName}, auto detailing, interior car cleaning`,
    checklistPoints: ["Interior vacuuming & shampoo", "Exterior hand wash", "Wax & polish", "Odor removal", "We come to you"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/11212d20-4d72-4d68-a362-89a23c8ee0fc.png"
  },
  "restaurant-hood-cleaning": {
    title: "Restaurant Hood Cleaning (Coming Soon)",
    description: `Commercial kitchen exhaust and hood cleaning for ${locationName} restaurants. Expanding our commercial services soon.`,
    keywords: `restaurant hood cleaning ${locationName}, commercial kitchen exhaust cleaning, fire code compliance`,
    checklistPoints: ["NFPA 96 compliance", "Grease removal", "Filter exchange", "Fan maintenance", "Certification provided"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/1e0c0a4e-0a0c-4858-800e-037e76674ae3.jpg"
  },
  "busy-families": {
    title: "Cleaning for Busy Families",
    description: `Juggling work, kids, and life in ${locationName}? Let us handle the cleaning. We create a safe, spotless environment for your family to thrive.`,
    keywords: `cleaning for busy families ${locationName}, family house cleaning, regular maid service`,
    checklistPoints: ["Kid-safe products available", "Toy tidying", "Kitchen sanitization", "Bathroom deep cleaning", "Flexible scheduling"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/d3b447ae-8f3f-4b3a-aad0-2305e1370261.png"
  },
  "before-guests": {
    title: "Cleaning Before Guests or Events",
    description: `Hosting an event in ${locationName}? Get your home guest-ready with our thorough pre-event cleaning services. Impress your guests with a spotless home.`,
    keywords: `pre event cleaning ${locationName}, cleaning before guests, party preparation cleaning`,
    checklistPoints: ["Living area detailing", "Guest bathroom sanitization", "Kitchen prep cleaning", "Floor polishing", "Dusting & shining"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/45693ca3-057a-4ac9-8c3f-50686ae15984.png"
  },
  "after-party": {
    title: "After Party Cleaning Service",
    description: `The party was great, but the mess in your ${locationName} home isn't. Let our team handle the post-event cleanup so you can rest.`,
    keywords: `after party cleaning ${locationName}, post event cleanup, morning after cleaning`,
    checklistPoints: ["Trash & recycling removal", "Spill cleanup", "Kitchen restoration", "Floor deep cleaning", "Bathroom sanitization"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/d9009cf2-193c-419e-ab3f-14ed63ffbc07.png"
  },
  "last-minute": {
    title: "Last-Minute Cleaning Service",
    description: `Need cleaning fast in ${locationName}? We offer rapid response last-minute cleaning for those unexpected moments.`,
    keywords: `last minute cleaning ${locationName}, emergency maid service, fast house cleaning`,
    checklistPoints: ["Rapid dispatch", "Priority scheduling", "Full team arrival", "Efficient deep clean", "Instant online booking"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/11212d20-4d72-4d68-a362-89a23c8ee0fc.png"
  },
  "move-preparation": {
    title: "Cleaning for Move Preparation",
    description: `Getting ready to sell your ${locationName} home? Our move preparation cleaning ensures your property looks its absolute best for showings.`,
    keywords: `move preparation cleaning ${locationName}, pre sale house cleaning, real estate cleaning`,
    checklistPoints: ["Open house staging clean", "Deep scrubbing", "Window track cleaning", "Appliance detailing", "Odor removal"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/45693ca3-057a-4ac9-8c3f-50686ae15984.png"
  },
  "seasonal": {
    title: "Seasonal Cleaning Services",
    description: `Spring cleaning or fall prep in ${locationName}? Refresh your home for the new season with our comprehensive seasonal cleaning.`,
    keywords: `seasonal cleaning ${locationName}, spring cleaning service, fall house cleaning`,
    checklistPoints: ["Baseboard & trim detail", "Ceiling fan dusting", "Cabinet exterior wash", "Deep floor care", "Window sill cleaning"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/d3b447ae-8f3f-4b3a-aad0-2305e1370261.png"
  },
  "homeowners-with-pets": {
    title: "Cleaning for Homeowners with Pets",
    description: `We love your furry friends! Our specialized pet cleaning service in ${locationName} tackles pet hair, dander, and odors effectively.`,
    keywords: `cleaning for pet owners ${locationName}, pet hair removal service, dog friendly house cleaning`,
    checklistPoints: ["Extensive vacuuming", "Pet odor neutralization", "Furniture hair removal", "Pet-safe products", "Floor sanitization"],
    image: "https://vibe.filesafe.space/1776798830316075366/assets/d9009cf2-193c-419e-ab3f-14ed63ffbc07.png"
  }
});

const ServicePage = () => {
  const { type } = useParams<{ type: string }>();
  const location = useLocation();
  const localContent = getLocalContent(location.name, location.regionSlug);

  const serviceData = getServiceData(location.name);
  const data = type ? serviceData[type as keyof typeof serviceData] : null;

  if (!data) return <Navigate to="/not-found" replace />;

  return (
    <div className="flex min-h-screen flex-col">
      <SEO 
        title={`${data.title} in ${location.name} | Uplevel Cleaning`}
        description={data.description}
        keywords={data.keywords}
        url={`https://uplevelcleaning.com/services/${type}`}
        locationName={location.name}
        includeLocalBusiness={true}
        includeService={true}
        serviceName={data.title}
        serviceDescription={data.description}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Services", url: "/services" },
          { name: data.title, url: `/services/${type}` }
        ]}
      />
      <Navbar />
      <main className="flex-1">
        {/* Service Hero */}
        <section className="relative py-20 lg:py-32 bg-background overflow-hidden">
          <div className="container relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 mb-6 bg-primary/10 text-primary px-4 py-2 rounded-full font-bold text-sm">
                  <Sparkles className="h-4 w-4" />
                  Premium {data.title} in {location.name}
                </div>
                <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                  {data.title} <br /> in {location.name}.
                </h1>
                <p className="text-xl text-muted-foreground mb-4">
                  {data.description}
                </p>
                <p className="text-base text-muted-foreground mb-8 border-l-4 border-primary pl-4">
                  {localContent.localIssuesText}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Button asChild size="lg" className="h-14 px-8 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary-hover">
                    <a href={BOOKING_URL}>
                      <Calendar className="mr-2 h-5 w-5" />
                      Book This Service
                    </a>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="h-14 px-8 text-lg font-bold border-2 border-primary text-primary hover:bg-primary/10 hover:text-primary">
                    <a href={BOOKING_URL} aria-label="Get instant price for specific service">
                      <Zap className="mr-2 h-5 w-5 fill-primary/20" />
                      Get Instant Price
                    </a>
                  </Button>
                </div>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {data.checklistPoints.map((point: string, i: number) => (
                    <li key={i} className="flex items-center gap-2 text-sm font-medium">
                      <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl border-8 border-background">
                  <img src={data.image} alt={`${data.title} in ${location.name}`} loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-card p-6 rounded-2xl shadow-xl border hidden md:block">
                  <div className="flex items-center gap-4">
                    <div className="bg-primary/10 p-3 rounded-xl">
                      <Clock className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-bold">Fast Booking</p>
                      <p className="text-sm text-muted-foreground">Under 60 seconds</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Strip */}
        <section className="py-10 bg-primary text-primary-foreground">
          <div className="container flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-xl font-bold">Ready for a spotless {location.name} home?</p>
              <p className="text-sm opacity-80">Book online in 60 seconds — transparent flat-rate pricing, no surprises.</p>
            </div>
            <div className="flex gap-4 shrink-0">
              <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 hover:text-primary font-bold">
                <a href={BOOKING_URL}>
                  <Calendar className="mr-2 h-5 w-5" />
                  Book Now
                </a>
              </Button>
              <Button asChild size="lg" className="bg-white/10 border border-white/30 text-white hover:bg-white/20 hover:text-white font-bold">
                <a href={BOOKING_URL} aria-label="Get instant price for service from footer">
                  <Zap className="mr-2 h-5 w-5 fill-white/20" />
                  Get Instant Price
                </a>
              </Button>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">What's Included in Our {data.title}</h2>
              <p className="text-muted-foreground">Transparent standards for a spotless home in {location.name}.</p>
            </div>
            <PlanComparison />
          </div>
        </section>

        <Guarantee />
        <HowItWorks />

        {/* Bottom CTA */}
        <section className="py-16 bg-background">
          <div className="container text-center">
            <h2 className="text-3xl font-bold mb-4">Book Your {data.title} in {location.name} Today</h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              Get your exact price in seconds with no commitment required.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="h-14 px-10 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary-hover">
                <a href={BOOKING_URL}>
                  <Calendar className="mr-2 h-5 w-5" />
                  Book Now
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-14 px-10 text-lg font-bold border-2 border-primary text-primary hover:bg-primary/10 hover:text-primary">
                <a href={BOOKING_URL} aria-label="Get instant price for service at bottom">
                  <Zap className="mr-2 h-5 w-5 fill-primary/20" />
                  Get Instant Price
                </a>
              </Button>
            </div>
          </div>
        </section>

        <FinalCTA />
        <InternalLinks />
      </main>
      <Footer />
    </div>
  );
};

export default ServicePage;
